import React, { useEffect } from 'react'
import axios from 'axios'
import { MeasureType } from 'enums'
import { createMetric, createSentenceRetrieval } from 'utils/runSearchUtil'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { getInsightContentData } from 'features/insightContent/asyncThunks'
import { getMetricSeries } from 'utils/measureTypeUtil'
import InsightContentType from 'components/insightContent/enum/InsightContentType'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'
import { SocialWebTimelineWidget as SocialWebTimelineWidgetType } from 'schemas/SocialWebTimelineWidgetSchema'
import { toFilter } from 'utils/widgetUtil'
import { InsightData } from 'schemas/action/InsightDataSchema'
import { getSparklineMetricTimeUnit } from 'utils/sparklineMetricTimeUnit'
import SortBy from 'enums/SortBy'
import { selectFluctuationPrerequisite } from 'features/insightContent/selectors'
import { ERROR_MESSAGE } from 'features/insightContent/insightContentSlice'
import { SENTENCES_FOR_EACH_TERM } from './constants'

const createSearches = (
  insightSet: InsightData,
  widgetSetting: SocialWebTimelineWidgetType,
  isAudienceQuery?: boolean
) => {
  const { precision, selectedTopic } = widgetSetting

  const filter = toFilter(widgetSetting)
  filter.phrases = filter.phrases || []
  const phrases = [insightSet.name].concat(insightSet.variants).filter(Boolean)
  filter.phrases.push(phrases)

  const searches = []
  const metricValueSearch = createMetric(
    -1,
    '',
    precision!,
    [selectedTopic.topicId!],
    filter,
    getMetricSeries(MeasureType.POSTS),
    [getSparklineMetricTimeUnit(filter?.publishedDate)],
    { isAudienceQuery }
  )
  const insightCountSearch = createSentenceRetrieval(
    -1,
    '',
    [selectedTopic.topicId!],
    [],
    filter,
    precision!,
    SortBy.AUTHOR_FOLLOWER,
    SENTENCES_FOR_EACH_TERM,
    true
  )
  searches.push(metricValueSearch, insightCountSearch)

  return searches
}

const RunSearchEffect = ({
  widgetSetting,
  children,
  isAudienceQuery
}: {
  widgetSetting: SocialWebTimelineWidgetType
  children: React.ReactNode
  isAudienceQuery?: boolean
}) => {
  const dispatch = useAppDispatch()
  const refreshTimestamp = useRefreshTimestamp()
  const { insightSets } = useAppSelector(selectFluctuationPrerequisite)

  useEffect(() => {
    // eslint-disable-next-line prefer-destructuring
    const CancelToken = axios.CancelToken
    const source = CancelToken.source()

    if (insightSets !== undefined) {
      const searches =
        insightSets
          ?.map((insightSet) => createSearches(insightSet, widgetSetting, isAudienceQuery))
          .flat() || []
      dispatch(
        getInsightContentData({
          insightContentType: InsightContentType.FLUCTUATION_POPULAR_TERMS,
          searches,
          timestamp: refreshTimestamp,
          requestConfig: {
            cancelToken: source.token
          }
        })
      )
    }

    return () => {
      source.cancel(ERROR_MESSAGE.REQUEST_CANCELLED)
    }
  }, [dispatch, widgetSetting, refreshTimestamp, isAudienceQuery, insightSets])

  return <>{children}</>
}

export default RunSearchEffect
